import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  signal,
} from '@angular/core';
import { useBreakpoint } from '../../composables/useBreakpoint';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

export interface CapNavigationItem {
  label: string;
  route: string[];
  ariaLabel: string;
}

@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './bottom-sheet.component.html',
  styleUrl: './bottom-sheet.component.scss',
})
export class BottomSheetComponent {
  @Input() capNavItems: CapNavigationItem[] = [];

  // Internal configuration - not exposed as Inputs since they're not customized
  private readonly initialHeightFraction = 0.7; // visible height fraction
  private readonly peekHeightPx = 24;
  private readonly overlayClosable = true;

  @Output() progressChange = new EventEmitter<number>(); // 0 at bottom, 1 at fully open

  readonly currentBreakpoint = useBreakpoint().currentBreakpoint;
  readonly isMobile = computed(
    () =>
      this.currentBreakpoint() === 'XSmall' ||
      this.currentBreakpoint() === 'Small'
  );

  @HostBinding('class.is-mobile')
  get hostIsMobile(): boolean {
    return this.isMobile();
  }

  public sheetOpen = signal(true);
  private dragging = signal(false);
  private startY = 0;
  private startOffset = 0;
  private offset = signal(0);

  ngOnInit(): void {
    // start open
    this.sheetOpen.set(true);
    this.offset.set(0);
    this.emitProgress();
  }

  get sheetStyleTransform(): string {
    return `translateY(${this.offset()}px)`;
  }

  get showGrabber(): boolean {
    // Show grabber only when sheet is at bottom (peek)
    const max = this.getMaxOffset();
    return this.offset() >= max - 1; // allow 1px tolerance
  }

  onOverlayClick(): void {
    if (!this.overlayClosable) return;
    this.close();
  }

  open(): void {
    this.sheetOpen.set(true);
    this.offset.set(0);
    this.emitProgress();
  }

  close(): void {
    this.sheetOpen.set(false);
    this.offset.set(this.getMaxOffset());
    this.emitProgress();
  }

  onDragStart(ev: PointerEvent): void {
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    this.dragging.set(true);
    this.startY = ev.clientY;
    this.startOffset = this.offset();
    window.addEventListener('pointermove', this.onDragMove, { passive: false });
    window.addEventListener('pointerup', this.onDragEnd, { once: true });
  }

  private onDragMove = (ev: PointerEvent) => {
    if (!this.dragging()) return;
    ev.preventDefault();
    const dy = ev.clientY - this.startY;
    const next = this.clamp(this.startOffset + dy, 0, this.getMaxOffset());
    this.offset.set(next);
    this.emitProgress();
  };

  private onDragEnd = (_ev: PointerEvent) => {
    this.dragging.set(false);
    window.removeEventListener('pointermove', this.onDragMove);
    const threshold = this.getMaxOffset() * 0.35;
    if (this.offset() > threshold) {
      this.close();
    } else {
      this.open();
    }
  };

  private getMaxOffset(): number {
    const vh = window.innerHeight || 800;
    const visibleHeight = Math.round(vh * this.initialHeightFraction);
    return Math.max(0, visibleHeight - this.peekHeightPx);
  }

  private emitProgress(): void {
    const max = this.getMaxOffset();
    const upProgress = max === 0 ? 0 : (max - this.offset()) / max; // 0 bottom, 1 open
    this.progressChange.emit(upProgress);
  }

  private clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
  }

  trackByLabel(_: number, item: CapNavigationItem): string {
    return item.label;
  }
}
